import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createElementBlock as _createElementBlock, unref as _unref, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = ["onMouseenter"]
const _hoisted_2 = {
  key: 0,
  class: "last-cell"
}

import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useGeneralStore } from '@/pinia'
import TableBase from '@/design-system/atoms/TableBase.vue'
import TextBase from '@/design-system/atoms/TextBase.vue'
import BadgeSquare from '@/design-system/atoms/BadgeSquare.vue'
import SvgDisplay from '@/design-system/atoms/SvgDisplay.vue'
import CardBase from '@/components/AnalyticsDashboard/components/CardBase.vue'
import { Table } from '@/design-system/atoms/table-base'
import TooltipStandardV2 from '@/design-system/atoms/TooltipStandardV2.vue'
import { useFetchTickets } from '@/api-client/models/tickets/useFetchTickets'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import { Ticket } from '@/api-client/models/tickets/Contracts'
import { getSingleTicket } from '@/services/tickets.service'
import { useHoverStore } from '@/pinia/hover'

type HeaderCell = {
  label: {
    title: string
    subtitle?: string
  }
}

type BodyCell = {
  cell: {
    index: number
    label: string
    id?: string
    badgeColor?: string
    backgroundColor?: string
    ticket: Ticket
  }
}

type ProjectTable = Table<HeaderCell, BodyCell>


export default /*@__PURE__*/_defineComponent({
  __name: 'TicketListTable',
  setup(__props) {

const piniaStore = useGeneralStore()

const hoveredIndex = ref<number | null>(null)
const showDetails = ref(false)
const selectedTicket = ref<Ticket | undefined>(undefined)
const { t } = useI18n()

const { itemList: filteredItems, loadMore, titleMap, totalTickets } = useFetchTickets()
const { addHoveredItem, removeLastHoveredItem } = useHoverStore()

const isFeatureFullscreen = computed(() => {
  return piniaStore.isFeatureFullscreen
})

const table = computed<ProjectTable>((): ProjectTable => {
  const headers: ProjectTable['headers'] = {
    top: [
      {
        label: {
          title: `${totalTickets.value} Tickets`
        },
        style: {
          width: '40%',
          height: '32px',
          padding: '4px',
          'background-color': '#D5D7F666'
        }
      },
      {
        label: {
          title: 'Typ'
        },
        style: {
          width: '35%',
          height: '32px',
          padding: '4px',
          'text-align': 'left',
          'background-color': '#D5D7F666'
        }
      }
    ]
  }
  const body: ProjectTable['body'] = filteredItems.value.map((ticket, index) => {
    return [
      {
        cell: {
          index: index,
          label: titleMap.value[ticket.ticketId],
          ticket
        },
        style: {
          width: '40%',
          padding: '4px',
          backgroundColor: hoveredIndex.value === index ? '#CDDEFE33' : 'transparent',
          cursor: 'pointer'
        }
      },
      {
        cell: {
          index: index,
          label: t(ticket.typeId),
          ticket
        },
        style: {
          width: '35%',
          padding: '4px',
          backgroundColor: hoveredIndex.value === index ? '#CDDEFE33' : 'transparent',
          cursor: 'pointer'
        }
      }
    ]
  })
  return {
    headers,
    body
  }
})

watch(selectedTicket, async (ticket) => {
  if (ticket) {
    const fullTicket = await getSingleTicket(ticket.ticketId)
    if (fullTicket) {
      piniaStore.setSelectedTicket(fullTicket)
      piniaStore.setCurrentOverlayModalPinia('editTicket')
      piniaStore.setShowOverlayModalPinia(true)
      selectedTicket.value = undefined
    }
  }
})

function onClickRow(row?: BodyCell[]) {
  if (!row || !row?.[1]?.cell.ticket) {
    return
  }
  selectedTicket.value = row?.[1].cell.ticket
  showDetails.value = true
}

function nearEnd(edge: 'top' | 'bottom') {
  if (edge === 'bottom') {
    loadMore()
  }
}

function getTicketEntityId(ticket: Ticket): string | undefined {
  if (ticket.spaceId) return ticket.spaceId
  if (ticket.levelId) return ticket.levelId
  if (ticket.constructionId) return ticket.constructionId
  return undefined
}

return (_ctx: any,_cache: any) => {
  return (table.value)
    ? (_openBlock(), _createBlock(CardBase, { key: 0 }, {
        default: _withCtx(() => [
          _createVNode(TableBase, {
            table: table.value,
            bodyHeight: isFeatureFullscreen.value ? 'auto' : '300px',
            layout: "fixed",
            rightTextAlign: "center",
            tabbableRows: true,
            caption: "Ticket table",
            onMouseEnterCell: _cache[1] || (_cache[1] = ($event: any) => (hoveredIndex.value = $event.cell.index)),
            onMouseLeaveCell: _cache[2] || (_cache[2] = ($event: any) => (hoveredIndex.value = null)),
            onOnClickRow: onClickRow,
            onNearEnd: nearEnd
          }, {
            "header-cell": _withCtx(({ cell: { label } }) => [
              _createVNode(TextBase, {
                text: label?.title,
                textWrap: "nowrap",
                type: 'paragraph',
                weight: 'bold'
              }, null, 8, ["text"])
            ]),
            "body-cell": _withCtx(({ cell, cellIndex }) => [
              _createElementVNode("div", {
                style: { cursor: 'pointer' },
                onMouseenter: 
            () => _unref(addHoveredItem)(getTicketEntityId(cell.cell.ticket) ?? '', { color: '#fadb14' })
          ,
                onMouseleave: _cache[0] || (_cache[0] = 
//@ts-ignore
(...args) => (_unref(removeLastHoveredItem) && _unref(removeLastHoveredItem)(...args)))
              }, [
                _createVNode(TooltipStandardV2, {
                  style: { height: '100%' },
                  text: cell.cell.label,
                  heatmap: "",
                  "arrow-position": "top-center"
                }, {
                  default: _withCtx(() => [
                    (cellIndex % 3 === 0 && cell.cell.label)
                      ? (_openBlock(), _createBlock(TextBase, {
                          key: 0,
                          text: cell.cell.label,
                          textWrap: "nowrap",
                          type: 'paragraph',
                          weight: 'regular',
                          overflow: 'ellipsis'
                        }, null, 8, ["text"]))
                      : (cellIndex % 3 === 0)
                        ? (_openBlock(), _createBlock(PulseLoader, {
                            key: 1,
                            loading: true,
                            color: "#3F46D6"
                          }))
                        : _createCommentVNode("", true)
                  ]),
                  _: 2
                }, 1032, ["text"]),
                (cellIndex % 3 === 1)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                      _createVNode(BadgeSquare, {
                        badgeText: cell.cell.label,
                        size: 'small',
                        color: 'primary',
                        style: { textAlign: 'left' }
                      }, null, 8, ["badgeText"]),
                      _createVNode(SvgDisplay, { src: 'Icon-Arrow-Up-Right_x16' })
                    ]))
                  : _createCommentVNode("", true)
              ], 40, _hoisted_1)
            ]),
            _: 1
          }, 8, ["table", "bodyHeight"])
        ]),
        _: 1
      }))
    : _createCommentVNode("", true)
}
}

})